import { useEffect, useRef, useMemo } from 'react';
import useGameStore from '@store/useGameStore';
import useMapStore from '@store/useMapStore';
import { getTileSize } from '@utils/brandHelper';
import { cancelAction } from '@services/actionService';
import ActionIcon from '@components/ui/game/action-icon/ActionIcon';
import UnitIcon from '@components/game/objects/UnitIcon';
import PlannedActionDelete from '@components/ui/game/planned-action/PlannedActionDelete';
import { Target } from 'lucide-react';
import { Team } from '@utils/enums';
import './AllActionsPanel.css';

/**
 * Panel that displays all planned actions as vertical cards
 *
 * @param {Object} props
 * @param {number} props.highlightedActionId - Action ID to highlight and scroll to
 * @param {function} props.onHighlightClear - Callback when highlight should be cleared
 */
export default function AllActionsPanel({ highlightedActionId, onHighlightClear }) {
  // Subscribe to the raw planned actions array to avoid creating new array on each render
  const rawPlannedActions = useGameStore(state => state.game?.team_planned_actions);
  const game = useGameStore(state => state.game);
  const centerOnTile = useMapStore(state => state.centerOnTile);

  // Memoize hydrated actions to prevent infinite loops
  const plannedActions = useMemo(() => {
    return useGameStore.getState().getHydratedPlannedActions();
  }, [rawPlannedActions]);

  // Sort actions by resolution order
  const sortedActions = useMemo(() => {
    return [...plannedActions].sort((a, b) => {
      const orderA = a.resolution_order || a.sequence_number || 999;
      const orderB = b.resolution_order || b.sequence_number || 999;
      return orderA - orderB;
    });
  }, [plannedActions]);

  // Ref for scrolling to highlighted action
  const actionRefs = useRef({});

  // Auto-scroll when highlightedActionId changes
  useEffect(() => {
    if (!highlightedActionId) return;

    // Scroll to the action after a small delay (for DOM to update)
    setTimeout(() => {
      const actionEl = actionRefs.current[highlightedActionId];
      if (actionEl) {
        actionEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }, [highlightedActionId]);

  const handleActionClick = (plannedAction) => {
    const targetPos = plannedAction.target_position || plannedAction.target_data;
    if (targetPos) {
      const tileSize = getTileSize();
      centerOnTile(targetPos.x, targetPos.y, tileSize);
    }

    // Clear highlight after clicking
    onHighlightClear?.();
  };

  const handleDeleteAction = async (actionId) => {
    if (!game?.id) {
      throw new Error('[AllActionsPanel] No game ID available for delete');
    }
    await cancelAction(game.id, actionId);
  };

  if (sortedActions.length === 0) {
    return (
      <div className="all-actions-panel">
        <div className="all-actions-empty">
          No planned actions
        </div>
      </div>
    );
  }

  return (
    <div className="all-actions-panel">
      <div className="all-actions-list">
        {sortedActions.map(plannedAction => {
          // FAIL LOUDLY: action.action MUST exist
          if (!plannedAction.action) {
            throw new Error(
              `[AllActionsPanel] PlannedAction ${plannedAction.id} missing action data. ` +
              `action_id: ${plannedAction.action_id}. This is a bug - action definition not found in game.actions`
            );
          }

          const targetPos = plannedAction.target_position || plannedAction.target_data;
          const resolutionOrder = plannedAction.resolution_order || plannedAction.sequence_number;
          const isHighlighted = plannedAction.id === highlightedActionId;
          const unit = useGameStore.getState().getUnitOrAvailableUnitById(plannedAction.unit_id);
          const teamClass = plannedAction.teamClass || unit?.teamClass || 'unknown';
          const isOwn = teamClass === Team.OWN;
          const unitName = unit?.unit_template?.name || unit?.name || `Unit #${plannedAction.unit_id}`;

          return (
            <div
              key={plannedAction.id}
              ref={el => actionRefs.current[plannedAction.id] = el}
              className={`all-actions-card ${isHighlighted ? 'all-actions-card-highlighted' : ''} team-${teamClass}`}
            >
              {/* Left: Action + Unit Info */}
              <button
                className="all-actions-card-content"
                onClick={() => handleActionClick(plannedAction)}
                type="button"
              >
                {/* Action Row */}
                <div className="all-actions-card-action">
                  <ActionIcon
                    action={plannedAction.action}
                    size={20}
                    teamClass={teamClass}
                  />
                  <span className="action-name">{plannedAction.action.name}</span>
                </div>

                {/* Unit Row */}
                <div className="all-actions-card-unit">
                  <UnitIcon unitId={plannedAction.unit_id} size={24} />
                  <span className="unit-name">{unitName}</span>
                  {targetPos && (
                    <span className="action-target">
                      <Target size={12} />
                      ({targetPos.x},{targetPos.y})
                    </span>
                  )}
                </div>
              </button>

              {/* Right: Order + Delete stacked vertically */}
              <div className="all-actions-card-sidebar">
                <span className="order-number">#{resolutionOrder || '?'}</span>
                {isOwn && (
                  <PlannedActionDelete
                    action={plannedAction}
                    onDelete={() => handleDeleteAction(plannedAction.id)}
                    size={18}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

